"use client";

import React from "react";
import {
  Box,
  Button,
  Typography,
  useTheme,
  alpha,
  Container,
} from "@mui/material";
import Image from "next/image";
import PopoverForm from "./PopoverForm";

const Contact = () => {
  const theme = useTheme();

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Box
        borderRadius={4}
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        {/* Background decorative elements */}
        <Box
          sx={{
            position: "absolute",
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -30,
            left: -30,
            width: 150,
            height: 150,
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
            zIndex: 1,
          }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: { xs: 4, md: 6 },
            gap: { xs: 4, md: 8 },
            justifyContent: "space-between",
            flexDirection: { xs: "column", lg: "row" },
            position: "relative",
            zIndex: 2,
          }}
        >
          {/* Text Content */}
          <Box sx={{ width: { xs: "100%", lg: "55%" } }}>
            <Typography
              variant="h2"
              sx={{
                mb: 3,
                fontWeight: 700,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                fontSize: { xs: "2rem", md: "2.5rem" },
                lineHeight: 1.2,
              }}
            >
              Empowering Your Growth – We&apos;re Here to Support You
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Typography
                component={"p"}
                variant="h6"
                sx={{
                  color: "text.primary",
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                Schedule a Discovery Call with Us:
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "text.secondary",
                  fontSize: "1.1rem",
                }}
              >
                We&apos;ll Respond Within 24 Hours
              </Typography>
            </Box>

            <PopoverForm
              triggerElement={
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    px: 6,
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    borderRadius: 2,
                    color: "white",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.3)}`,
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Let&apos;s Talk
                </Button>
              }
            />
          </Box>

          {/* Image Gallery */}
          <Box
            sx={{
              position: "relative",
              width: { xs: "100%", lg: "40%" },
              minHeight: { xs: 300, md: 400 },
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: 300,
                height: 300,
              }}
            >
              {/* Main Image */}
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: 200,
                  height: 200,
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: `4px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  boxShadow: `0 10px 30px ${alpha(theme.palette.primary.main, 0.2)}`,
                  zIndex: 4,
                }}
              >
                <Image
                  src="/contact1.jpg"
                  alt="Customer service team"
                  fill
                  sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 400px"
                  style={{
                    objectFit: "cover",
                  }}
                />
              </Box>

              {/* Secondary Image */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  width: 120,
                  height: 120,
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: `3px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                  boxShadow: `0 8px 25px ${alpha(theme.palette.secondary.main, 0.15)}`,
                  zIndex: 3,
                }}
              >
                <Image
                  src="/contact2.jpg"
                  alt="Team collaboration"
                  fill
                  sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 400px"
                  style={{
                    objectFit: "cover",
                  }}
                />
              </Box>

              {/* Tertiary Image */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: 100,
                  height: 100,
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: `3px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.15)}`,
                  zIndex: 3,
                }}
              >
                <Image
                  src="/contact3.jpg"
                  alt="Business meeting"
                  fill
                  sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 400px"
                  style={{
                    objectFit: "cover",
                  }}
                />
              </Box>

              {/* Decorative dots */}
              <Box
                sx={{
                  position: "absolute",
                  top: "20%",
                  left: "10%",
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  opacity: 0.3,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: "30%",
                  right: "15%",
                  width: 15,
                  height: 15,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                  opacity: 0.3,
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default Contact;
